import { ArrowLeft, MessageSquareText } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { TurnstileWidget } from '@/components/ui/TurnstileWidget';
import { useT } from '@/lib/i18n';
import { DEFAULT_WHATSAPP_COUNTRY_CODE, WHATSAPP_COUNTRY_CODES } from '@/lib/phone';
import type { CaptchaChallenge } from '@/lib/turnstile';
import { useCaptcha } from '@/lib/useCaptcha';

import {
  completeWhatsAppSignup,
  requestWhatsAppCode,
  verifyWhatsAppCode,
  type ReferralPayload,
  type SocialSession,
} from '../../api/socialAuth';
import { WhatsAppIcon } from './BrandIcons';

const CODE_LENGTH = 6;

type Step = { name: 'phone' } | { name: 'code'; phone: string; display: string };

function messageFor(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** `+91 98765 43210`, the way the person typed it rather than as the API stores it. */
function displayNumber(countryCode: string, national: string): string {
  return `+${countryCode} ${national.replace(/\s+/g, ' ').trim()}`;
}

export interface WhatsAppSignInProps {
  mode: 'login' | 'register';
  referral?: ReferralPayload;
  /** The Turnstile check to show before a code is asked for, when it always is. */
  captcha?: CaptchaChallenge | null;
  onBack: () => void;
  onAuthenticated: (session: SocialSession) => Promise<void> | void;
}

/**
 * Number, then code. A number we have not seen gets its account straight
 * away - no name, no email - so a merchant reaches onboarding in two steps;
 * the account carries a stand-in address until they add one in My Account.
 *
 * Every step keeps its own error beside the control that has to change, and
 * nothing is thrown away on a mistake: a wrong code keeps the number.
 */
export function WhatsAppSignIn({
  mode,
  referral,
  captcha,
  onBack,
  onAuthenticated,
}: WhatsAppSignInProps) {
  const t = useT();
  const [step, setStep] = useState<Step>({ name: 'phone' });

  return (
    <div>
      <button
        type="button"
        onClick={step.name === 'code' ? () => setStep({ name: 'phone' }) : onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4 rtl-flip" aria-hidden />
        {step.name === 'code' ? t('auth.whatsapp.other_number') : t('auth.whatsapp.all_options')}
      </button>

      <div className="mt-4 flex items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[#25D366]/10">
          <WhatsAppIcon className="size-6" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-ink">
            {mode === 'register'
              ? t('auth.social.sign_up_with', { provider: 'WhatsApp' })
              : t('auth.social.continue_with', { provider: 'WhatsApp' })}
          </p>
          <p className="text-sm text-muted">
            {step.name === 'phone'
              ? t('auth.whatsapp.phone_intro')
              : t('auth.whatsapp.code_sent_to', { number: step.display })}
          </p>
        </div>
      </div>

      <div className="mt-5">
        {step.name === 'phone' ? (
          <PhoneStep
            captcha={captcha ?? null}
            onSent={(phone, display) => setStep({ name: 'code', phone, display })}
          />
        ) : (
          <CodeStep
            phone={step.phone}
            display={step.display}
            captcha={captcha ?? null}
            onVerified={async (result) => {
              if (result.kind === 'signed-in') {
                await onAuthenticated(result.session);
                return;
              }
              // A new number: the ticket alone opens the account. A failure
              // here surfaces on the code step, which is where a retry starts.
              const session = await completeWhatsAppSignup({
                signup_token: result.signupToken,
                ...referral,
              });
              await onAuthenticated(session);
            }}
          />
        )}
      </div>
    </div>
  );
}

function PhoneStep({
  captcha: initialChallenge,
  onSent,
}: {
  captcha: CaptchaChallenge | null;
  onSent: (phone: string, display: string) => void;
}) {
  const captcha = useCaptcha(initialChallenge);
  const t = useT();
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_WHATSAPP_COUNTRY_CODE);
  const [national, setNational] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const selectId = useId();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const digits = national.replace(/\D/g, '');
    if (digits.length < 6) {
      setError(t('auth.whatsapp.number_required'));
      return;
    }
    if (captcha.pending) {
      setError(t('auth.whatsapp.captcha_first'));
      return;
    }
    setError(null);
    setSending(true);
    const phone = `+${countryCode}${digits}`;
    const token = captcha.token ?? undefined;
    try {
      await requestWhatsAppCode(phone, token);
      onSent(phone, displayNumber(countryCode, national));
    } catch (caught) {
      if (token) captcha.spend();
      captcha.absorb(caught);
      // Already sent moments ago: that code still works, so go and use it.
      if (caught instanceof ApiError && caught.code === 'CODE_RESEND_TOO_SOON') {
        onSent(phone, displayNumber(countryCode, national));
        return;
      }
      setError(messageFor(caught, t('auth.common.generic_error')));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div>
        <label htmlFor={`${selectId}-number`} className="mb-1.5 block text-sm font-medium text-ink">
          {t('auth.whatsapp.number_label')}
        </label>
        <div
          className={
            'flex h-11 w-full overflow-hidden rounded-xl border bg-white focus-within:ring-2 ' +
            (error
              ? 'border-red-300 focus-within:border-red-400 focus-within:ring-red-100'
              : 'border-line focus-within:border-brand-400 focus-within:ring-brand-100')
          }
        >
          <label htmlFor={selectId} className="sr-only">
            {t('auth.whatsapp.country_code')}
          </label>
          <select
            id={selectId}
            value={countryCode}
            onChange={(event) => setCountryCode(event.target.value)}
            className="h-full shrink-0 border-r border-line bg-surface pl-3 pr-2 text-sm font-medium text-ink focus:outline-none"
          >
            {WHATSAPP_COUNTRY_CODES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            id={`${selectId}-number`}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            placeholder="98765 43210"
            value={national}
            onChange={(event) => setNational(event.target.value)}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${selectId}-error` : undefined}
            className="h-full min-w-0 flex-1 px-3.5 text-sm text-ink placeholder:text-muted focus:outline-none"
          />
        </div>
        {error ? (
          <p
            id={`${selectId}-error`}
            role="alert"
            className="mt-1.5 text-sm font-medium text-red-600"
          >
            {error}
          </p>
        ) : null}
      </div>

      {captcha.challenge ? (
        <TurnstileWidget
          key={captcha.widgetKey}
          challenge={captcha.challenge}
          onToken={captcha.setToken}
        />
      ) : null}

      <Button type="submit" size="lg" className="w-full" disabled={sending}>
        {sending ? (
          <>
            <Spinner size="sm" /> {t('auth.whatsapp.sending_code')}
          </>
        ) : (
          t('auth.whatsapp.send_code')
        )}
      </Button>
      <p className="text-center text-xs text-muted">
        {t('auth.whatsapp.messaging_note')}
      </p>
    </form>
  );
}

function CodeStep({
  phone,
  display,
  captcha: initialChallenge,
  onVerified,
}: {
  phone: string;
  display: string;
  captcha: CaptchaChallenge | null;
  onVerified: (
    result: Awaited<ReturnType<typeof verifyWhatsAppCode>>,
  ) => Promise<void> | void;
}) {
  const t = useT();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(30);
  const captcha = useCaptcha(initialChallenge);
  const inputRef = useRef<HTMLInputElement>(null);
  // The last code submitted, so auto-submit does not fire twice for one entry.
  const submitted = useRef<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const verify = async (value: string) => {
    if (value.length !== CODE_LENGTH || submitted.current === value) return;
    submitted.current = value;
    setError(null);
    setNotice(null);
    setVerifying(true);
    try {
      const result = await verifyWhatsAppCode(phone, value);
      await onVerified(result);
    } catch (caught) {
      submitted.current = null;
      if (caught instanceof ApiError && caught.code === 'CODE_INVALID') {
        const left = caught.details?.attempts_left;
        setError(
          typeof left === 'number'
            ? t('auth.code.wrong', { count: left })
            : caught.message,
        );
      } else {
        setError(messageFor(caught, t('auth.common.generic_error')));
      }
      setCode('');
      inputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setError(null);
    setNotice(null);
    if (captcha.pending) {
      setError(t('auth.whatsapp.captcha_resend'));
      return;
    }
    setResending(true);
    const token = captcha.token ?? undefined;
    try {
      const sent = await requestWhatsAppCode(phone, token);
      if (token) captcha.spend();
      setNotice(t('auth.code.resent', { email: display }));
      setResendIn(sent.resend_after || 30);
      setCode('');
      submitted.current = null;
      inputRef.current?.focus();
    } catch (caught) {
      if (token) captcha.spend();
      captcha.absorb(caught);
      const retry = caught instanceof ApiError ? caught.details?.retry_after : undefined;
      if (typeof retry === 'number') setResendIn(retry);
      setError(messageFor(caught, t('auth.common.generic_error')));
    } finally {
      setResending(false);
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (code.length !== CODE_LENGTH) {
          setError(t('auth.code.enter_digits', { length: CODE_LENGTH }));
          return;
        }
        void verify(code);
      }}
      noValidate
      className="space-y-4"
    >
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <div>
        <label htmlFor="whatsapp-code" className="mb-1.5 block text-sm font-medium text-ink">
          {t('auth.code.label')}
        </label>
        <input
          ref={inputRef}
          id="whatsapp-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={CODE_LENGTH}
          placeholder="••••••"
          value={code}
          disabled={verifying}
          onChange={(event) => {
            const next = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
            setCode(next);
            if (error) setError(null);
            if (next.length === CODE_LENGTH) void verify(next);
          }}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? 'whatsapp-code-error' : undefined}
          className={
            'h-14 w-full rounded-xl border bg-white text-center font-mono text-2xl tracking-[0.5em] text-ink ' +
            'placeholder:text-line focus:outline-none focus:ring-2 disabled:opacity-60 ' +
            (error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-line focus:border-brand-400 focus:ring-brand-100')
          }
        />
        {error ? (
          <p
            id="whatsapp-code-error"
            role="alert"
            className="mt-1.5 text-sm font-medium text-red-600"
          >
            {error}
          </p>
        ) : (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted">
            <MessageSquareText className="size-3.5" aria-hidden />
            {t('auth.whatsapp.code_hint')}
          </p>
        )}
      </div>

      <Button type="submit" size="lg" className="w-full" disabled={verifying}>
        {verifying ? (
          <>
            <Spinner size="sm" /> {t('auth.code.verifying')}
          </>
        ) : (
          t('auth.code.submit')
        )}
      </Button>

      {resendIn <= 0 && captcha.challenge ? (
        <TurnstileWidget
          key={captcha.widgetKey}
          challenge={captcha.challenge}
          onToken={captcha.setToken}
        />
      ) : null}

      <p className="text-center text-sm text-muted" aria-live="polite">
        {t('auth.code.not_received')}{' '}
        {resendIn > 0 ? (
          <span>{t('auth.code.resend_in', { seconds: resendIn })}</span>
        ) : (
          <button
            type="button"
            onClick={() => void resend()}
            disabled={resending}
            className="font-semibold text-brand-600 hover:underline disabled:opacity-60"
          >
            {resending ? t('auth.common.sending') : t('auth.code.resend')}
          </button>
        )}
      </p>
    </form>
  );
}
